/**
 * Individual (B2C) onboarding WRAPPER store — Zustand + persist.
 *
 * The Phase-1 end-customer onboarding (`/onboarding/individual`) is a stepped
 * state machine that WRAPS the existing document-first doc-dump flow. This store
 * holds the FOUR outer gates the wrapper owns BEFORE the doc-dump portion runs,
 * and they are consumed IN THIS ORDER (2026-08-18 — see `outerPhase` in
 * `routes/onboarding/individual-screen.tsx`):
 *
 *   1. `provisioned`      — the empty b2c tenant has been minted (onboard POST)
 *                           + the session refreshed so the JWT carries the new
 *                           tenant_id + role. Until this is true the doc-dump
 *                           setup endpoints would 403 (no tenant on the claims).
 *   2. `mfaDone`          — a TOTP authenticator was enrolled + verified inline.
 *   3. `welcomeDone`      — the customer has seen the welcome (who are we setting
 *                           this up for?) and the overview (what we collect, why,
 *                           and that accuracy matters). Pure orientation, no
 *                           network call — but a real gate so a refresh doesn't
 *                           replay it.
 *   4. `subscriptionDone` — the (STUBBED, no real charge) Paystack subscription
 *                           seam has been called (`POST /v1/setup/subscription`).
 *
 * The subscription gate USED to sit at position 2. Paul's 2026-08-18 ruling moved
 * it to immediately before Documents: document ingestion is the step that costs
 * real money, so the payment gate belongs on the cost rather than two steps early
 * on a stranger who has not yet seen anything work.
 *
 * `expectedTrusts` (default 1) is captured on the subscription step's stepper —
 * it drives the live monthly-total preview AND pre-seeds the doc-dump
 * questionnaire's trust count (so the customer doesn't answer "how many trusts?"
 * twice). The doc-dump's OWN progress (questionnaire → upload → … → done) lives
 * in `setup-store`, NOT here — this store only tracks the three pre-doc-dump
 * gates + the seeded trust count.
 *
 * Persisted under a versioned key so a refresh mid-onboarding resumes at the
 * right outer step (e.g. provisioned-but-not-subscribed lands back on the
 * subscription step rather than re-minting a tenant). `clear()` resets it (the
 * doc-dump done step clears the setup-store; this is cleared alongside on exit).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/** WHO PAYS for this individual's subscription. Set from the self-join profile
 *  when a client joins a firm under Client Model B:
 *   - 'firm'   → the firm covers it → the wrapper SKIPS the paid subscription step.
 *   - 'client' → the client pays (a normal b2c-direct individual) → the stubbed
 *                subscription step runs as today.
 *   - null     → not yet known / plain b2c-direct (treated like 'client'). */
export type BillingOwner = 'firm' | 'client' | null;

/** WHO the customer told us they are, on the welcome screen. Orientation only —
 *  it tailors the overview copy and nothing else. It is NOT a claim of authority
 *  over any trust: a trustee still proves that the same way anyone does, by the
 *  deed and letters of authority we read. `null` = not answered yet. */
export type PrincipalRole = 'founder' | 'trustee' | null;

export interface IndividualOnboardingStore {
  /** The empty b2c tenant has been minted + the session refreshed. */
  provisioned: boolean;
  /** The (stubbed) subscription seam has been called. */
  subscriptionDone: boolean;
  /** A TOTP authenticator was enrolled + verified. */
  mfaDone: boolean;
  /** The welcome + overview screens have been seen and acknowledged. */
  welcomeDone: boolean;
  /** Who the customer said they are on the welcome screen (copy only). */
  principalRole: PrincipalRole;
  /** How many trusts the customer expects — drives the monthly preview + pre-seeds
   *  the doc-dump questionnaire. Default 1 (the base includes the first trust). */
  expectedTrusts: number;
  /** WHO PAYS — set from the self-join profile (Client Model B). 'firm' skips the
   *  paid subscription step; 'client'/null keeps it. */
  billingOwner: BillingOwner;

  setProvisioned: (v: boolean) => void;
  setSubscriptionDone: (v: boolean) => void;
  setMfaDone: (v: boolean) => void;
  setWelcomeDone: (v: boolean) => void;
  setPrincipalRole: (v: PrincipalRole) => void;
  setExpectedTrusts: (n: number) => void;
  setBillingOwner: (v: BillingOwner) => void;
  /** Reset all outer-gate state (called on exit / restart). */
  clear: () => void;
}

// v2 (2026-06-22): added `billingOwner` (Client Model B).
// v3 (2026-08-18): added `welcomeDone` + `principalRole` (the welcome/overview
// gate). Bumping the version drops any older persisted state mid-onboarding
// rather than rehydrating a shape without the new fields — acceptable since the
// only loss is the resumable outer gates, which the wrapper re-derives
// (provisioning re-checks the JWT tenant, MFA re-checks the live factor list,
// the subscription re-reads its server manifest breadcrumb). The one gate that
// does NOT re-derive is `welcomeDone`, whose worst case is being shown the
// welcome screen a second time.
const STORAGE_KEY = 'elreign:individual-onboarding:v3';

/** The default expected-trust count — the base plan includes the first trust. */
export const DEFAULT_EXPECTED_TRUSTS = 1;

export const useIndividualOnboardingStore = create<IndividualOnboardingStore>()(
  persist(
    (set) => ({
      provisioned: false,
      subscriptionDone: false,
      mfaDone: false,
      welcomeDone: false,
      principalRole: null,
      expectedTrusts: DEFAULT_EXPECTED_TRUSTS,
      billingOwner: null,

      setProvisioned: (provisioned) => set({ provisioned }),
      setSubscriptionDone: (subscriptionDone) => set({ subscriptionDone }),
      setMfaDone: (mfaDone) => set({ mfaDone }),
      setWelcomeDone: (welcomeDone) => set({ welcomeDone }),
      setPrincipalRole: (principalRole) => set({ principalRole }),
      // Clamp to a sane floor (≥1 — you're setting up at least one trust); a
      // non-finite value falls back to the default.
      setExpectedTrusts: (n) =>
        set({
          expectedTrusts: Number.isFinite(n) ? Math.max(1, Math.floor(n)) : DEFAULT_EXPECTED_TRUSTS,
        }),
      setBillingOwner: (billingOwner) => set({ billingOwner }),

      clear: () =>
        set({
          provisioned: false,
          subscriptionDone: false,
          mfaDone: false,
          welcomeDone: false,
          principalRole: null,
          expectedTrusts: DEFAULT_EXPECTED_TRUSTS,
          billingOwner: null,
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 3,
    },
  ),
);
